import { DatePicker, Form, Input, Select, Tooltip } from "antd-v5";
import message from "components/CustomMessage";
import { PermissionSetMap } from "constants/RBACConstants";
import dayjs, { Dayjs } from "dayjs";
import useDebounceCallback from "hooks/useDebounceCallback";
import { useCallback, useEffect } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { CRAComplianceData, CRAState } from "types/craCompliance";
import { hasPermission } from "utils/authService";
import {
  DEFAULT_CRA_STATE,
  EXPLOIT_STATUS_OPTIONS,
  FIELD_TOOLTIPS,
  NOTIFICATION_STATUS_OPTIONS,
  TOOLTIP_OVERLAY_STYLE,
  USER_DISCLOSURE_STATUS_OPTIONS,
  craDataToState,
  craStateToApiData,
} from "utils/craComplianceUtils";

import ACAppLoader from "ac-components/ac-app-loader/ACAppLoader";
import InfoIcon from "ac-components/ac-icons/InfoIcon";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";

import "./ComplianceTab.less";
import { useFindingDetails } from "./FindingDetailsProvider";

const { TextArea } = Input;

export default function ComplianceTab() {
  const {
    finding,
    loading,
    id: findingId,
    updateFindingDetails,
  } = useFindingDetails() as {
    finding: { compliance?: { cra?: CRAComplianceData } } | undefined;
    loading: boolean;
    id: number | undefined;
    updateFindingDetails: (partial: Record<string, unknown>) => void;
  };

  const craData: CRAComplianceData | null = finding?.compliance?.cra ?? null;

  const [form] = Form.useForm<CRAState>();

  useEffect(() => {
    form.setFieldsValue(craData ? craDataToState(craData) : DEFAULT_CRA_STATE);
  }, [findingId, craData, form]);

  const saveAllFields = useCallback(
    (allValues: CRAState) => {
      if (!findingId) return;
      const apiData = craStateToApiData(allValues);
      const { promise } = FindingAPI.updateCRACompliance(findingId, {
        cra: apiData,
      });
      promise
        .then((res: { data: CRAComplianceData; message?: string }) => {
          const msg = res?.message || "Compliance data saved successfully";
          message.success(msg);
          updateFindingDetails({ compliance: { cra: res.data } });
        })
        .catch(
          (err: {
            response?: { data?: { message?: string } };
            message?: string;
          }) => {
            const errorMsg =
              err?.response?.data?.message ||
              err?.message ||
              "Failed to save compliance data";
            message.error(errorMsg);
          },
        );
    },
    [findingId, updateFindingDetails],
  );

  const debouncedSave = useDebounceCallback(
    (allValues: CRAState) => saveAllFields(allValues),
    [saveAllFields],
    500,
  );

  const handleValuesChange = useCallback(
    (craValue: Partial<CRAState>, allValues: CRAState) => {
      if ("remediationEvidence" in craValue) {
        debouncedSave(allValues);
      } else {
        saveAllFields(allValues);
      }
    },
    [saveAllFields, debouncedSave],
  );

  const disablePastDates = useCallback(
    (current: Dayjs) => current?.isBefore(dayjs().startOf("day")),
    [],
  );

  if (loading) return <ACAppLoader />;

  return (
    <div className="compliance-tab">
      <ACSettingSectionTitle
        title="Cyber Resilience Act"
        description="Information related to the EU Cyber Resilience Act"
      />
      <Form form={form} onValuesChange={handleValuesChange}>
        <div className="compliance-form">
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              Exploit Status
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.exploitStatus}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="exploitStatus" noStyle>
                <Select
                  className="compliance-select"
                  options={EXPLOIT_STATUS_OPTIONS}
                  optionLabelProp="label"
                  placeholder="Select status"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              Notification Status
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.notificationStatus}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="notificationStatus" noStyle>
                <Select
                  className="compliance-select"
                  options={NOTIFICATION_STATUS_OPTIONS}
                  optionLabelProp="label"
                  placeholder="Select status"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              Early Warning Due
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.earlyWarningDue}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="earlyWarningDue" noStyle>
                <DatePicker
                  className="compliance-date-picker"
                  disabledDate={disablePastDates}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                  showNow={false}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              Notification Due
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.notificationDue}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="notificationDue" noStyle>
                <DatePicker
                  className="compliance-date-picker"
                  disabledDate={disablePastDates}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                  showNow={false}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              Final Report Due
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.finalReportDue}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="finalReportDue" noStyle>
                <DatePicker
                  className="compliance-date-picker"
                  disabledDate={disablePastDates}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                  showNow={false}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              User Disclosure Status
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.userDisclosureStatus}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="userDisclosureStatus" noStyle>
                <Select
                  className="compliance-select"
                  options={USER_DISCLOSURE_STATUS_OPTIONS}
                  optionLabelProp="label"
                  placeholder="Select status"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row">
            <div className="compliance-form-label">
              User Disclosure Due
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.userDisclosureDue}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="userDisclosureDue" noStyle>
                <DatePicker
                  className="compliance-date-picker"
                  disabledDate={disablePastDates}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                  showNow={false}
                />
              </Form.Item>
            </div>
          </div>
          <div className="compliance-form-row compliance-form-row-top">
            <div className="compliance-form-label">
              Remediation Evidence
              <Tooltip
                placement="right"
                overlayStyle={TOOLTIP_OVERLAY_STYLE}
                title={FIELD_TOOLTIPS.remediationEvidence}
              >
                <span className="compliance-label-tooltip-icon">
                  <InfoIcon />
                </span>
              </Tooltip>
            </div>
            <div className="compliance-form-value">
              <Form.Item name="remediationEvidence" noStyle>
                <TextArea
                  className="compliance-textarea"
                  rows={4}
                  placeholder=""
                  maxLength={1000}
                  showCount
                  disabled={!hasPermission(PermissionSetMap.UPDATE_FINDINGS)}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}
